import {
  SettingsGroup,
  SettingsHr,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { SettingsProps } from './multiple-choice-settings.constants';
import { FormProvider, useForm } from 'react-hook-form';
import { RangeFieldSetting } from '../../shared/range-field';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import {
  ACTIVITIES_RESPONSE_VARIANT_TEXT,
  ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE,
  COMPONENT_LAYOUT_GRID,
  COMPONENT_LAYOUT_LIST,
  LANGUAGE_CODE_EN_AU,
  POSITION_RIGHT,
  POSITION_TOP,
} from 'global/constants';
import { getRowAndGridOptions } from './multiple-choice-settings.utils';
import {
  canShowOptionLetter,
  getDefaultRowAndGridLayout,
} from '../multiple-choice.utils';
import { useMultipleChoiceUpdate } from './multiple-choice-settings.hooks';
import Switch from 'approot/shared/switch/switch';

const OPTION_HEIGHT_FIELD = 'optionHeightField';

type FormData = {
  [OPTION_HEIGHT_FIELD]: number;
};

const MultipleChoiceSettingsLayout = ({
  sectionId,
  id,
  type,
  sort,
  properties,
  subActivityProperties,
  suggestedAnswerProperties,
  version,
}: SettingsProps) => {
  const { handleUpdate } = useMultipleChoiceUpdate({
    id,
    sectionId,
    type,
    sort,
    properties,
    subActivityProperties,
    suggestedAnswerProperties,
    language: LANGUAGE_CODE_EN_AU,
    version,
  });

  const formMethods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      [OPTION_HEIGHT_FIELD]: properties.multipleChoice.optionMinHeight || 80,
    },
  });
  const { watch } = formMethods;
  const optionHeightField = watch(OPTION_HEIGHT_FIELD);

  return (
    <SettingsGroup as="fieldset" marginBottom={0}>
      <ScreenReaderOnly as="legend">
        Multiple Choice Settings Layout
      </ScreenReaderOnly>

      {properties.multipleChoice.variant ===
        ACTIVITIES_RESPONSE_VARIANT_TEXT && (
        <SettingsGroup>
          <SettingsLabel>Layout</SettingsLabel>
          <CheckableSingle
            name="optionsLayout"
            selected={properties.layout?.display}
            options={[
              {
                label: 'Grid',
                value: COMPONENT_LAYOUT_GRID,
              },
              {
                label: 'List',
                value: COMPONENT_LAYOUT_LIST,
              },
            ]}
            onSelect={display =>
              handleUpdate({
                ...properties,
                layout: {
                  __typename: 'ComponentLayout',
                  display,
                },
              })
            }
          />
        </SettingsGroup>
      )}

      {properties.layout?.display === COMPONENT_LAYOUT_GRID && (
        <SettingsGroup>
          <SettingsLabel>Rows x Columns</SettingsLabel>
          <CheckableSingle
            name="rowsAndColumnsLayout"
            selected={
              properties.multipleChoice.rowsGridLayout ||
              getDefaultRowAndGridLayout(
                properties.multipleChoice.options.length
              )
            }
            options={getRowAndGridOptions(
              properties.multipleChoice.options.length
            ).map(i => ({
              label: i,
              value: i,
            }))}
            onSelect={display =>
              handleUpdate({
                ...properties,
                multipleChoice: {
                  ...properties.multipleChoice,
                  rowsGridLayout: display,
                },
              })
            }
          />
        </SettingsGroup>
      )}

      {!(
        properties.multipleChoice.variant ===
          ACTIVITIES_RESPONSE_VARIANT_TEXT &&
        properties.layout?.display === COMPONENT_LAYOUT_LIST
      ) && (
        <FormProvider {...formMethods}>
          <SettingsGroup marginBottom={0}>
            <RangeFieldSetting
              fieldName={OPTION_HEIGHT_FIELD}
              label="Choices Height"
              max={262}
              min={80}
              postfix="px"
              value={optionHeightField}
              onChange={size =>
                handleUpdate({
                  ...properties,
                  multipleChoice: {
                    ...properties.multipleChoice,
                    optionMinHeight: size,
                  },
                })
              }
            />
          </SettingsGroup>
        </FormProvider>
      )}
      <SettingsHr />

      <SettingsGroup>
        <SettingsLabel>Show Letter Options</SettingsLabel>
        <Switch
          state={canShowOptionLetter(
            properties.multipleChoice.showOptionLetter
          )}
          ariaLabel="Toggle to show letters beside options"
          onClick={() =>
            handleUpdate({
              ...properties,
              multipleChoice: {
                ...properties.multipleChoice,
                showOptionLetter: !canShowOptionLetter(
                  properties.multipleChoice.showOptionLetter
                ),
              },
            })
          }
        />
      </SettingsGroup>

      {properties.multipleChoice.variant ===
        ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE && (
        <>
          <SettingsHr />
          <SettingsGroup>
            <SettingsLabel>Image position in relation to text</SettingsLabel>
            <CheckableSingle
              name="imagePosition"
              selected={
                properties.multipleChoice.imagePosition || POSITION_RIGHT
              }
              options={[
                {
                  label: 'Right',
                  value: POSITION_RIGHT,
                },
                {
                  label: 'Above',
                  value: POSITION_TOP,
                },
              ]}
              onSelect={imagePosition =>
                handleUpdate({
                  ...properties,
                  multipleChoice: {
                    ...properties.multipleChoice,
                    imagePosition,
                  },
                })
              }
            />
          </SettingsGroup>
        </>
      )}
    </SettingsGroup>
  );
};

export default MultipleChoiceSettingsLayout;
